import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Link
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import { useDispatch, useSelector } from 'react-redux';
import {
  exportDefaultFares,
  fetchDefaultFares,
  importDefaultFares,
  syncDefaultFares,
  updateDefaultFares
} from '../store/slices/defaultFareSlice';

const DefaultFarePage = () => {
  const dispatch = useDispatch();
  const {
    stations,
    matrix,
    loading,
    error,
    savingStatus,
    savingError,
    uploadStatus,
    uploadError,
    exportStatus,
    exportError,
    syncStatus,
    syncError,
    updatedAt
  } = useSelector((state) => state.defaultFares);

  const [editMatrix, setEditMatrix] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [reloadDialogOpen, setReloadDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedUploadFile, setSelectedUploadFile] = useState(null);
  const [uploadValidationError, setUploadValidationError] = useState('');

  useEffect(() => {
    dispatch(fetchDefaultFares());
  }, [dispatch]);

  const createEditableMatrix = useCallback(
    (sourceMatrix) =>
      (sourceMatrix || []).map((row) =>
        row.map((cell) => ({
          ...cell,
          price:
            cell.price === null || cell.price === undefined || Number.isNaN(cell.price)
              ? ''
              : String(cell.price)
        }))
      ),
    []
  );

  useEffect(() => {
    if (!isEditing) {
      setEditMatrix(createEditableMatrix(matrix));
    }
  }, [matrix, isEditing, createEditableMatrix]);

  useEffect(() => {
    if (savingStatus === 'succeeded') {
      setIsEditing(false);
      setSuccessMessage('Default fares updated successfully.');
    } else if (savingStatus === 'failed') {
      setSuccessMessage('');
    } else if (savingStatus === 'loading') {
      setSuccessMessage('');
    }
  }, [savingStatus]);

  const handleToggleEdit = () => {
    setSuccessMessage('');
    setEditMatrix(createEditableMatrix(matrix));
    setIsEditing(true);
  };

  const handleCancel = () => {
    setEditMatrix(createEditableMatrix(matrix));
    setIsEditing(false);
    setSuccessMessage('');
  };

  const handlePriceChange = (rowIndex, colIndex, value) => {
    const sanitized = value.replace(/[^\d]/g, '');
    setEditMatrix((prev) =>
      prev.map((row, rIdx) =>
        row.map((cell, cIdx) =>
          rIdx === rowIndex && cIdx === colIndex ? { ...cell, price: sanitized } : cell
        )
      )
    );
  };

  const handleSave = async () => {
    const payloadMatrix = editMatrix.map((row) =>
      row.map((cell) => ({
        origin: cell.origin,
        destination: cell.destination,
        price: Number(cell.price) || 0
      }))
    );

    try {
      await dispatch(updateDefaultFares({ matrix: payloadMatrix })).unwrap();
    } catch (saveError) {
      // Error is handled via savingError state; no-op here to satisfy linting.
    }
  };

  const handleReload = async () => {
    setSuccessMessage('');
    try {
      await dispatch(syncDefaultFares()).unwrap();
      await dispatch(fetchDefaultFares());
      setSuccessMessage('Fare matrix reloaded successfully.');
    } catch (reloadError) {
      // Error handled via syncError state.
    }
  };

  const handleUpload = async () => {
    setSuccessMessage('');
    setUploadValidationError('');
    if (!selectedUploadFile) {
      return;
    }

    try {
      await dispatch(importDefaultFares(selectedUploadFile)).unwrap();
      await dispatch(fetchDefaultFares());
      setSuccessMessage('Fare matrix uploaded successfully.');
      setUploadDialogOpen(false);
      setSelectedUploadFile(null);
    } catch (uploadErrorMessage) {
      // Error handled by uploadError state so the dialog can show resultDesc.
    }
  };

  const isReloading = syncStatus === 'loading';
  const disableReload = isReloading || savingStatus === 'loading';
  const isUploading = uploadStatus === 'loading';
  const isExporting = exportStatus === 'loading';
  const allowedUploadMimeTypes = [
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  ];

  const actionButton = isEditing ? (
    <Stack direction="row" spacing={1}>
      <Button
        variant="outlined"
        startIcon={<CloseOutlinedIcon />}
        onClick={handleCancel}
        sx={{ minWidth: 120 }}
        disabled={savingStatus === 'loading'}
      >
        Cancel
      </Button>
      <Button
        variant="contained"
        startIcon={<SaveOutlinedIcon />}
        onClick={handleSave}
        sx={{ minWidth: 120 }}
        disabled={savingStatus === 'loading'}
      >
        {savingStatus === 'loading' ? 'Saving...' : 'Save'}
      </Button>
    </Stack>
  ) : (
    <Stack direction="row" spacing={1.5}>
      <Button
        variant="outlined"
        onClick={() => setReloadDialogOpen(true)}
        sx={{ minWidth: 120 }}
        disabled={disableReload || loading}
      >
        {isReloading ? 'Reloading...' : 'Reload'}
      </Button>
      <Button
        variant="outlined"
        startIcon={<UploadFileOutlinedIcon />}
        onClick={() => setUploadDialogOpen(true)}
        sx={{ minWidth: 170 }}
        disabled={loading}
      >
        Upload Fare Matrix
      </Button>
      <Button
        variant="contained"
        startIcon={<EditOutlinedIcon />}
        onClick={handleToggleEdit}
        sx={{ minWidth: 140 }}
        disabled={loading}
      >
        Edit Fare Matrix
      </Button>
    </Stack>
  );

  const stationList = Array.isArray(stations) ? stations : [];
  const matrixToRender = (isEditing ? editMatrix : matrix) || [];
  const hasMatrix = Array.isArray(matrixToRender) && matrixToRender.length > 0 && stationList.length > 0;

  return (
    <Stack spacing={3}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        spacing={2}
      >
        <Box>
          <Breadcrumbs
            aria-label="breadcrumb"
            separator="/"
            sx={{ fontSize: 12, mb: 1, color: 'text.secondary' }}
          >
            <Link underline="hover" color="inherit" href="#">
              Dashboard
            </Link>
            <Link underline="hover" color="inherit" href="#">
              Management
            </Link>
            <Typography color="text.primary">Default Fare</Typography>
          </Breadcrumbs>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Default Fare Matrix
          </Typography>
          <Typography color="text.secondary">
            Configure default fares between each station pair.
          </Typography>
          {updatedAt && (
            <Typography variant="caption" color="text.secondary">
              Last updated: {updatedAt}
            </Typography>
          )}
        </Box>
        {actionButton}
      </Stack>

      {error && (
        <Alert severity="error">
          {error}
        </Alert>
      )}
      {syncError && <Alert severity="error">{syncError}</Alert>}
      {savingError && (
        <Alert severity="error">{savingError}</Alert>
      )}
      {successMessage && (
        <Alert severity="success" onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}

      {loading && !hasMatrix ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Stack spacing={2} alignItems="center">
            <CircularProgress size={32} />
            <Typography color="text.secondary">Loading default fares...</Typography>
          </Stack>
        </Paper>
      ) : (
      <Paper sx={{ overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    position: 'sticky',
                    left: 0,
                    zIndex: 3,
                    backgroundColor: 'background.paper',
                    fontWeight: 700
                  }}
                >
                  OD
                </TableCell>
                {stationList.map((code) => (
                  <TableCell key={`header-${code}`} sx={{ fontWeight: 700 }}>
                    {code}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {matrixToRender.map((row, rowIndex) => (
                <TableRow key={`row-${stationList[rowIndex] || rowIndex}`}>
                  <TableCell
                    sx={{
                      position: 'sticky',
                      left: 0,
                      zIndex: 2,
                      backgroundColor: 'background.paper',
                      fontWeight: 600
                    }}
                  >
                    {stationList[rowIndex] || '-'}
                  </TableCell>
                  {row.map((cell, colIndex) => (
                    <TableCell key={`${cell.origin}-${cell.destination}`}>
                      {isEditing ? (
                        <TextField
                          value={cell.price}
                          onChange={(event) =>
                            handlePriceChange(rowIndex, colIndex, event.target.value)
                          }
                          size="small"
                          fullWidth
                          inputProps={{
                            inputMode: 'decimal',
                            sx: { textAlign: 'center', fontWeight: 600 }
                          }}
                          sx={{
                            minWidth: { xs: 96 }                            
                          }}
                        />
                      ) : (
                        <Typography
                          variant="body2"
                          sx={{ textAlign: 'center', fontWeight: 600 }}
                        >
                          {Number(cell.price || 0).toLocaleString()}
                        </Typography>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      )}

      <Dialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Upload Fare Matrix</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            <DialogContentText>
              Upload a fare matrix file and use the sample template below as the expected format.
            </DialogContentText>
            {uploadValidationError ? <Alert severity="error">{uploadValidationError}</Alert> : null}
            {uploadError ? <Alert severity="error">{uploadError}</Alert> : null}
            {exportError ? <Alert severity="error">{exportError}</Alert> : null}
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              alignItems={{ sm: 'center' }}
            >
              <Button component="label" startIcon={<UploadFileOutlinedIcon />}>
                Choose File
                <input
                  hidden
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(event) => {
                    const nextFile = event.target.files?.[0] || null;

                    if (!nextFile) {
                      setSelectedUploadFile(null);
                      setUploadValidationError('');
                      return;
                    }

                    const isValidMimeType = allowedUploadMimeTypes.includes(nextFile.type);
                    const isValidExtension = /\.(xlsx|xls)$/i.test(nextFile.name);

                    if (!isValidMimeType && !isValidExtension) {
                      setSelectedUploadFile(null);
                      setUploadValidationError('Only Excel files (.xlsx or .xls) are allowed.');
                      return;
                    }

                    setSelectedUploadFile(nextFile);
                    setUploadValidationError('');
                  }}
                />
              </Button>
              <Typography variant="body2" color="text.secondary">
                {selectedUploadFile ? selectedUploadFile.name : 'No file selected'}
              </Typography>
            </Stack>
            <Link
              component="button"
              type="button"
              underline="hover"
              disabled={isExporting}
              onClick={() => dispatch(exportDefaultFares())}
              sx={{
                alignSelf: 'flex-start',
                background: 'none',
                border: 0,
                p: 0,
                cursor: isExporting ? 'default' : 'pointer'
              }}
            >
              {isExporting ? 'Preparing current data file...' : 'Download Current Data File'}
            </Link>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => {
              setUploadDialogOpen(false);
              setSelectedUploadFile(null);
              setUploadValidationError('');
            }}
            disabled={isUploading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            disabled={!selectedUploadFile || isUploading}
          >
            {isUploading ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={16} color="inherit" />
                <span>Uploading...</span>
              </Stack>
            ) : (
              'Upload'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={reloadDialogOpen}
        onClose={() => setReloadDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reload Fare Matrix</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will sync the fare matrix from the central server.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setReloadDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={async () => {
              await handleReload();
              setReloadDialogOpen(false);
            }}
            disabled={disableReload || loading}
          >
            {isReloading ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={16} color="inherit" />
                <span>Reloading...</span>
              </Stack>
            ) : (
              'Ok'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default DefaultFarePage;
